import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * First-person bridge interior of the frigate, built procedurally around the
 * camera. Everything lives in camera space (metres): eye at the origin, looking
 * down -Z, +Y up. The group is meant to be parented to the camera.
 *
 * Layout: a tapered "nose tunnel" of hull panels converging on an octagonal
 * forward window, braced by ribs and angled A-pillars, with an overhead switch
 * panel, a glare-shielded dashboard carrying three displays, and side consoles
 * (NAV display on the left, throttle quadrant on the right).
 */

export type CockpitReadout = {
  speed: string;
  altitude: string;
  nearest: string;
  target: string;
  targetDistance: string;
  throttle: number;
  shield: number;
  warp: 'off' | 'spool' | 'on' | 'locked';
  warpCharge: number;
  boosting: boolean;
  radar: { x: number; y: number; z: number; kind: 'rock' | 'player' | 'target' }[];
  timeSeconds: number;
};

// ---------------------------------------------------------------------------
// Palette / constants
// ---------------------------------------------------------------------------

const CYAN = '#7fe7ff';
const WHITE = '#ffffff';
const WARN = '#ff7a45';
const GOLD = '#ffd27a';
const DIM = 'rgba(127,231,255,0.55)';
const FAINT = 'rgba(127,231,255,0.18)';
const FONT_FAMILY = '"Chakra Petch", sans-serif';
const font = (px: number, weight = 600): string => `${weight} ${px}px ${FONT_FAMILY}`;

const RO_OPAQUE = 20;
const RO_DETAIL = 21;
const RO_SCREEN = 22;
const RO_GLASS = 25;

/** Forward window outline (x, y) at Z_WIN, clockwise from top-left. */
const WIN: [number, number][] = [
  [-1.05, 0.8],
  [1.05, 0.8],
  [1.55, 0.38],
  [1.6, -0.45],
  [1.4, -0.95],
  [-1.4, -0.95],
  [-1.6, -0.45],
  [-1.55, 0.38],
];
const Z_WIN = -1.75;
const Z_NEAR = -0.45;
const NEAR_SCALE = 0.62;
/** Annunciator strip hangs under the header rib. */
const ANN_Y = 0.598;
const ANN_Z = -1.335;
const FLOOR_EDGE = 4; // WIN[4] -> WIN[5]: hidden under the dashboard, not built

const V3 = THREE.Vector3;
type Vec3 = THREE.Vector3;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function octScale(z: number): number {
  return 1 + ((z - Z_WIN) / (Z_NEAR - Z_WIN)) * (NEAR_SCALE - 1);
}

function octAt(z: number): Vec3[] {
  const s = octScale(z);
  return WIN.map(([x, y]) => new V3(x * s, y * s, z));
}

/** Inward (towards the view axis) 2D normal of window edge i, as a Vector3 with z = 0. */
function edgeInward(i: number): Vec3 {
  const [ax, ay] = WIN[i];
  const [bx, by] = WIN[(i + 1) % WIN.length];
  const n = new V3(-(by - ay), bx - ax, 0).normalize();
  if (n.x * (ax + bx) + n.y * (ay + by) > 0) n.negate();
  return n;
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('CockpitInterior: 2D canvas unavailable');
  return ctx;
}

// ---------------------------------------------------------------------------
// Geometry batching: many primitives -> one merged, box-projected mesh
// ---------------------------------------------------------------------------

class Bin {
  private parts: THREE.BufferGeometry[] = [];

  add(g: THREE.BufferGeometry, m?: THREE.Matrix4): void {
    const geo = g.index ? g.toNonIndexed() : g.clone();
    g.dispose();
    if (m) geo.applyMatrix4(m);
    for (const name of Object.keys(geo.attributes)) {
      if (name !== 'position' && name !== 'normal') geo.deleteAttribute(name);
    }
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    this.parts.push(geo);
  }

  box(w: number, h: number, d: number, m: THREE.Matrix4): void {
    this.add(new THREE.BoxGeometry(w, h, d), m);
  }

  build(uvPerMetre: number): THREE.BufferGeometry {
    const merged = mergeGeometries(this.parts, false);
    for (const p of this.parts) p.dispose();
    this.parts = [];
    if (!merged) throw new Error('CockpitInterior: merge failed');
    boxProjectUV(merged, uvPerMetre);
    return merged;
  }
}

/** Per-triangle planar projection on the dominant axis: uniform texel density everywhere. */
function boxProjectUV(g: THREE.BufferGeometry, scale: number): void {
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const uv = new Float32Array(pos.count * 2);
  const a = new V3();
  const b = new V3();
  const c = new V3();
  const n = new V3();
  for (let i = 0; i + 2 < pos.count; i += 3) {
    a.fromBufferAttribute(pos, i);
    b.fromBufferAttribute(pos, i + 1);
    c.fromBufferAttribute(pos, i + 2);
    n.subVectors(c, b).cross(new V3().subVectors(a, b));
    const ax = Math.abs(n.x);
    const ay = Math.abs(n.y);
    const az = Math.abs(n.z);
    for (let k = 0; k < 3; k++) {
      const p = k === 0 ? a : k === 1 ? b : c;
      let u: number;
      let v: number;
      if (ax >= ay && ax >= az) {
        u = p.z;
        v = p.y;
      } else if (ay >= az) {
        u = p.x;
        v = p.z;
      } else {
        u = p.x;
        v = p.y;
      }
      uv[(i + k) * 2] = u * scale;
      uv[(i + k) * 2 + 1] = v * scale;
    }
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** Basis with local +Z along a->b, local +Y along `up` (orthogonalised), centred between a and b. */
function beamMatrix(a: Vec3, b: Vec3, up: Vec3): { m: THREE.Matrix4; len: number } {
  const z = new V3().subVectors(b, a);
  const len = z.length();
  z.divideScalar(len);
  const y = up.clone().addScaledVector(z, -up.dot(z)).normalize();
  const x = new V3().crossVectors(y, z);
  const m = new THREE.Matrix4().makeBasis(x, y, z);
  m.setPosition(new V3().addVectors(a, b).multiplyScalar(0.5));
  return { m, len };
}

function trs(x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    new V3(x, y, z),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)),
    new V3(1, 1, 1),
  );
}

function mul(parent: THREE.Matrix4, local: THREE.Matrix4): THREE.Matrix4 {
  return new THREE.Matrix4().multiplyMatrices(parent, local);
}

// ---------------------------------------------------------------------------
// Procedural textures
// ---------------------------------------------------------------------------

const LABELS = [
  'PWR BUS 3A', 'HYD 07', 'COOLANT', 'O2 LINE B', 'EPS 12', 'AUX 4', 'NAV-B', 'DATA 2C',
  'FR-117', 'FR-118', 'SERVICE', 'CAUTION', 'GRD', 'LIFE SUP', 'SHLD CPL 2', 'FDR 9', 'MAIN BUS',
];

function makePanelTextures(rand: () => number): { map: THREE.CanvasTexture; bump: THREE.CanvasTexture } {
  const S = 1024;
  const c = makeCanvas(S, S);
  const b = makeCanvas(S, S);
  const g = ctx2d(c);
  const h = ctx2d(b);
  g.fillStyle = '#8c939c';
  g.fillRect(0, 0, S, S);
  h.fillStyle = '#808080';
  h.fillRect(0, 0, S, S);

  // Micro speckle.
  for (let i = 0; i < 9000; i++) {
    const dark = rand() < 0.6;
    g.fillStyle = dark ? `rgba(0,0,0,${rand() * 0.07})` : `rgba(255,255,255,${rand() * 0.05})`;
    g.fillRect(rand() * S, rand() * S, 1 + rand() * 3, 1 + rand() * 2);
  }

  // Recursive panel split.
  const rects: [number, number, number, number][] = [];
  const split = (x: number, y: number, w: number, hh: number, d: number): void => {
    if (d > 4 || w < 150 || hh < 150 || (d > 1 && rand() < 0.28)) {
      rects.push([x, y, w, hh]);
      return;
    }
    const f = 0.3 + rand() * 0.4;
    if (w > hh ? rand() < 0.8 : rand() < 0.2) {
      split(x, y, w * f, hh, d + 1);
      split(x + w * f, y, w * (1 - f), hh, d + 1);
    } else {
      split(x, y, w, hh * f, d + 1);
      split(x, y + hh * f, w, hh * (1 - f), d + 1);
    }
  };
  split(0, 0, S, S, 0);

  for (const [x, y, w, hh] of rects) {
    const tone = (rand() - 0.5) * 0.16;
    g.fillStyle = tone > 0 ? `rgba(255,255,255,${tone})` : `rgba(0,0,0,${-tone})`;
    g.fillRect(x, y, w, hh);
    const bt = 128 + (rand() - 0.5) * 30;
    h.fillStyle = `rgb(${bt},${bt},${bt})`;
    h.fillRect(x + 3, y + 3, w - 6, hh - 6);

    // Grime gradient towards the bottom of the panel.
    const gr = g.createLinearGradient(0, y, 0, y + hh);
    gr.addColorStop(0, 'rgba(0,0,0,0)');
    gr.addColorStop(1, `rgba(20,16,12,${0.05 + rand() * 0.12})`);
    g.fillStyle = gr;
    g.fillRect(x, y, w, hh);

    // Seam: dark groove + highlight lip.
    g.strokeStyle = 'rgba(8,10,12,0.9)';
    g.lineWidth = 4;
    g.strokeRect(x, y, w, hh);
    g.strokeStyle = 'rgba(255,255,255,0.18)';
    g.lineWidth = 1;
    g.strokeRect(x + 3.5, y + 3.5, w - 7, hh - 7);
    h.strokeStyle = '#101010';
    h.lineWidth = 6;
    h.strokeRect(x, y, w, hh);

    // Screws.
    const inset = 16;
    for (const [sx, sy] of [
      [x + inset, y + inset],
      [x + w - inset, y + inset],
      [x + inset, y + hh - inset],
      [x + w - inset, y + hh - inset],
    ]) {
      g.fillStyle = 'rgba(10,12,14,0.8)';
      g.beginPath();
      g.arc(sx, sy, 6, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = 'rgba(190,198,208,0.8)';
      g.beginPath();
      g.arc(sx, sy, 4, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = 'rgba(20,22,26,0.9)';
      g.lineWidth = 1.5;
      const a = rand() * Math.PI;
      g.beginPath();
      g.moveTo(sx - Math.cos(a) * 3.5, sy - Math.sin(a) * 3.5);
      g.lineTo(sx + Math.cos(a) * 3.5, sy + Math.sin(a) * 3.5);
      g.stroke();
      h.fillStyle = '#d0d0d0';
      h.beginPath();
      h.arc(sx, sy, 4, 0, Math.PI * 2);
      h.fill();
    }

    const roll = rand();
    if (roll < 0.14 && w > 200) {
      // Vent slots.
      const rows = 3 + Math.floor(rand() * 4);
      const vx = x + 30;
      const vy = y + hh * 0.35;
      const vw = Math.min(w - 60, 260);
      for (let r = 0; r < rows; r++) {
        g.fillStyle = 'rgba(6,7,9,0.9)';
        g.fillRect(vx, vy + r * 16, vw, 7);
        g.fillStyle = 'rgba(255,255,255,0.12)';
        g.fillRect(vx, vy + r * 16 + 7, vw, 1);
        h.fillStyle = '#202020';
        h.fillRect(vx, vy + r * 16, vw, 7);
      }
    } else if (roll < 0.22 && w > 160) {
      // Hazard stripe band.
      const bx = x + 24;
      const by = y + hh - 44;
      const bw = Math.min(w - 48, 180);
      g.save();
      g.beginPath();
      g.rect(bx, by, bw, 16);
      g.clip();
      g.fillStyle = 'rgba(210,160,40,0.55)';
      g.fillRect(bx, by, bw, 16);
      g.fillStyle = 'rgba(15,15,15,0.7)';
      for (let k = -20; k < bw + 20; k += 20) {
        g.beginPath();
        g.moveTo(bx + k, by + 16);
        g.lineTo(bx + k + 10, by + 16);
        g.lineTo(bx + k + 26, by);
        g.lineTo(bx + k + 16, by);
        g.closePath();
        g.fill();
      }
      g.restore();
    }
    if (rand() < 0.45) {
      g.font = font(17);
      g.textBaseline = 'top';
      g.fillStyle = 'rgba(225,235,245,0.5)';
      g.fillText(LABELS[Math.floor(rand() * LABELS.length)], x + 30, y + 26);
      if (rand() < 0.5) {
        g.font = font(12, 400);
        g.fillStyle = 'rgba(225,235,245,0.35)';
        g.fillText(`${Math.floor(rand() * 900 + 100)}-${Math.floor(rand() * 90 + 10)}`, x + 30, y + 46);
      }
    }
  }

  // Soft stains.
  for (let i = 0; i < 26; i++) {
    const x = rand() * S;
    const y = rand() * S;
    const r = 30 + rand() * 120;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, `rgba(18,14,10,${0.05 + rand() * 0.1})`);
    gr.addColorStop(1, 'rgba(18,14,10,0)');
    g.fillStyle = gr;
    g.fillRect(x - r, y - r, r * 2, r * 2);
  }

  const map = new THREE.CanvasTexture(c);
  map.colorSpace = THREE.SRGBColorSpace;
  const bump = new THREE.CanvasTexture(b);
  for (const t of [map, bump]) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 8;
  }
  return { map, bump };
}

function makeBrushedTexture(rand: () => number): THREE.CanvasTexture {
  const S = 512;
  const c = makeCanvas(S, S);
  const g = ctx2d(c);
  g.fillStyle = '#9ea6af';
  g.fillRect(0, 0, S, S);
  for (let i = 0; i < 5000; i++) {
    const y = rand() * S;
    const x = rand() * S;
    const len = 40 + rand() * 300;
    g.strokeStyle = rand() < 0.5 ? `rgba(255,255,255,${rand() * 0.12})` : `rgba(0,0,0,${rand() * 0.12})`;
    g.lineWidth = 0.5 + rand();
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + len, y);
    g.stroke();
    if (x + len > S) {
      g.beginPath();
      g.moveTo(x - S, y);
      g.lineTo(x + len - S, y);
      g.stroke();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

function makeGlassAlpha(rand: () => number): THREE.CanvasTexture {
  const S = 512;
  const c = makeCanvas(S, S);
  const g = ctx2d(c);
  g.fillStyle = 'rgb(60,60,60)';
  g.fillRect(0, 0, S, S);
  // Edge dirt / condensation.
  const edge = (x0: number, y0: number, x1: number, y1: number): void => {
    const gr = g.createLinearGradient(x0, y0, x1, y1);
    gr.addColorStop(0, 'rgba(255,255,255,0.85)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, S, S);
  };
  edge(0, 0, 70, 0);
  edge(S, 0, S - 70, 0);
  edge(0, S, 0, S - 90);
  edge(0, 0, 0, 50);
  // Smudges and streaks.
  for (let i = 0; i < 40; i++) {
    const x = rand() * S;
    const y = rand() * S;
    const r = 10 + rand() * 50;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, `rgba(255,255,255,${0.08 + rand() * 0.2})`);
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  for (let i = 0; i < 20; i++) {
    const x = rand() * S;
    const y = S - rand() * 110;
    g.strokeStyle = `rgba(255,255,255,${0.1 + rand() * 0.15})`;
    g.lineWidth = 1 + rand() * 2;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + (rand() - 0.5) * 20, y - 20 - rand() * 60);
    g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

// ---------------------------------------------------------------------------
// LEDs
// ---------------------------------------------------------------------------

const enum LedMode {
  Steady,
  Blink,
  Pulse,
  Flicker,
  Throttle,
  Reverse,
  Warning,
  Warp,
}

type Led = { color: THREE.Color; mode: LedMode; period: number; phase: number; param: number };

type Screen = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  tex: THREE.CanvasTexture;
  overlay: HTMLCanvasElement;
  w: number;
  h: number;
  draw: (s: Screen, r: CockpitReadout, t: number) => void;
};

type Annunciator = { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial };

// ---------------------------------------------------------------------------
// CockpitInterior
// ---------------------------------------------------------------------------

export class CockpitInterior {
  readonly group = new THREE.Group();

  private readonly root = new THREE.Group();
  private readonly rand = mulberry32(0x5b1d9e);
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly textures: THREE.Texture[] = [];

  private readonly keyLight: THREE.PointLight;
  private readonly washLight: THREE.PointLight;
  private readonly keyIntensity = 1.6;
  private readonly washIntensity = 0.7;

  private readonly leds: Led[] = [];
  private ledMesh!: THREE.InstancedMesh;
  private readonly accentMat: THREE.MeshBasicMaterial;
  private readonly accentBase = new THREE.Color(0x3fc4e6);

  private readonly screens: Screen[] = [];
  private redrawTimer = 0;
  private time = 0;

  private readonly leverArm = new THREE.Group();
  private leverAngle = 0;

  private readonly annCaution: Annunciator;
  private readonly annWarp: Annunciator;
  private readonly annBoost: Annunciator;

  private visible = true;
  private readonly tmpColor = new THREE.Color();

  // Shared materials.
  private readonly hullMat: THREE.MeshStandardMaterial;
  private readonly trimMat: THREE.MeshStandardMaterial;
  private readonly metalMat: THREE.MeshStandardMaterial;

  constructor() {
    this.group.name = 'CockpitInterior';
    this.group.add(this.root);

    const { map, bump } = makePanelTextures(this.rand);
    const brushed = makeBrushedTexture(this.rand);
    this.textures.push(map, bump, brushed);

    this.hullMat = this.opaque(
      new THREE.MeshStandardMaterial({
        color: 0x5a636e,
        map,
        bumpMap: bump,
        bumpScale: 1.2,
        metalness: 0.55,
        roughness: 0.62,
        emissive: new THREE.Color(0x0b1016),
        envMapIntensity: 1.2,
      }),
    );
    this.trimMat = this.opaque(
      new THREE.MeshStandardMaterial({
        color: 0x23272c,
        map,
        bumpMap: bump,
        bumpScale: 0.6,
        metalness: 0.2,
        roughness: 0.85,
        emissive: new THREE.Color(0x05070a),
      }),
    );
    this.metalMat = this.opaque(
      new THREE.MeshStandardMaterial({
        color: 0xb4bcc6,
        map: brushed,
        metalness: 0.9,
        roughness: 0.32,
        emissive: new THREE.Color(0x0c1014),
        envMapIntensity: 1.6,
      }),
    );
    this.accentMat = new THREE.MeshBasicMaterial({ color: this.accentBase.clone(), toneMapped: false });
    this.opaque(this.accentMat);

    // Interior lighting (children of the group, not of `root`, so hiding keeps light count stable).
    this.keyLight = new THREE.PointLight(0xd4ecff, this.keyIntensity, 4, 2);
    this.keyLight.position.set(0, 0.22, -0.35);
    this.washLight = new THREE.PointLight(0x7fe7ff, this.washIntensity, 1.8, 2);
    this.washLight.position.set(0, -0.48, -0.85);
    this.group.add(this.keyLight, this.washLight);

    const hull = new Bin();
    const trim = new Bin();
    const metal = new Bin();
    const accent = new Bin();

    this.buildShell(hull, metal, trim, accent);
    this.buildDashboard(hull, trim, accent);
    this.buildConsoles(hull, trim, metal, accent);
    this.buildOverhead(hull, trim, metal, accent);
    this.annCaution = this.buildAnnunciator('MASTER CAUTION', -0.21, trim);
    this.annWarp = this.buildAnnunciator('WARP DRIVE', 0, trim);
    this.annBoost = this.buildAnnunciator('BOOST', 0.21, trim);
    this.buildLever();

    this.addMesh(hull.build(0.8), this.hullMat, RO_OPAQUE);
    this.addMesh(trim.build(1.3), this.trimMat, RO_OPAQUE);
    this.addMesh(metal.build(1.5), this.metalMat, RO_OPAQUE);
    this.addMesh(accent.build(1), this.accentMat, RO_DETAIL);

    this.buildGlass();
    this.buildLedMesh();
    this.setVisible(true);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.root.visible = v;
    // Keep lights in the scene (avoids shader recompiles on toggle), just dark.
    this.keyLight.intensity = v ? this.keyIntensity : 0;
    this.washLight.intensity = v ? this.washIntensity : 0;
    if (v) this.redrawTimer = 0;
  }

  update(dt: number, r: CockpitReadout): void {
    if (!this.visible) return;
    this.time += dt;
    const t = this.time;

    // Throttle lever: -0.15 -> pulled back, 1 -> fully forward.
    const thr = THREE.MathUtils.clamp(r.throttle, -0.15, 1);
    const target = THREE.MathUtils.lerp(0.5, -0.55, (thr + 0.15) / 1.15);
    this.leverAngle += (target - this.leverAngle) * (1 - Math.exp(-14 * dt));
    this.leverArm.rotation.x = this.leverAngle;

    const lowShield = r.shield < 0.3;
    const pulse = 0.5 + 0.5 * Math.sin(t * 7);

    // Accent strips: cyan, washing to amber when shields are low.
    this.tmpColor.copy(this.accentBase);
    if (lowShield) this.tmpColor.set(WARN).multiplyScalar(0.45 + 0.55 * pulse);
    else if (r.warp === 'on') this.tmpColor.lerp(new THREE.Color(0x9fb4ff), 0.4);
    this.accentMat.color.copy(this.tmpColor);
    this.washLight.color.set(lowShield ? 0xffb08a : 0x7fe7ff);

    // Annunciators.
    this.setAnn(this.annCaution, lowShield ? WARN : null, lowShield ? 0.35 + 0.65 * pulse : 0);
    const warpCol = r.warp === 'on' ? CYAN : r.warp === 'spool' ? GOLD : r.warp === 'locked' ? WARN : null;
    const warpLvl = r.warp === 'spool' ? 0.55 + 0.45 * Math.sin(t * 10) ** 2 : 1;
    this.setAnn(this.annWarp, warpCol, warpLvl);
    this.setAnn(this.annBoost, r.boosting ? GOLD : null, 1);

    this.updateLeds(t, r);

    this.redrawTimer -= dt;
    if (this.redrawTimer <= 0) {
      this.redrawTimer = 0.1;
      for (const s of this.screens) {
        s.draw(s, r, t);
        s.ctx.drawImage(s.overlay, 0, 0);
        s.tex.needsUpdate = true;
      }
    }
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
    for (const t of this.textures) t.dispose();
    this.ledMesh.dispose();
    this.keyLight.dispose();
    this.washLight.dispose();
  }

  // -------------------------------------------------------------------------
  // Construction
  // -------------------------------------------------------------------------

  private opaque<M extends THREE.Material>(m: M): M {
    m.transparent = true;
    m.depthWrite = true;
    m.side = THREE.DoubleSide;
    m.forceSinglePass = true;
    this.materials.push(m);
    return m;
  }

  private addMesh(g: THREE.BufferGeometry, m: THREE.Material, order: number): THREE.Mesh {
    const mesh = new THREE.Mesh(g, m);
    mesh.renderOrder = order;
    mesh.frustumCulled = false;
    this.geometries.push(g);
    this.root.add(mesh);
    return mesh;
  }

  /** Tapered nose tunnel, window rim, ribs, pillars and cable runs. */
  private buildShell(hull: Bin, metal: Bin, trim: Bin, accent: Bin): void {
    const W = octAt(Z_WIN);
    const N = octAt(Z_NEAR);
    const n = WIN.length;

    // Hull walls.
    const pos: number[] = [];
    for (let i = 0; i < n; i++) {
      if (i === FLOOR_EDGE) continue;
      const j = (i + 1) % n;
      const q = [W[i], W[j], N[j], N[i]];
      for (const k of [0, 1, 2, 0, 2, 3]) pos.push(q[k].x, q[k].y, q[k].z);
    }
    const walls = new THREE.BufferGeometry();
    walls.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    walls.computeVertexNormals();
    hull.add(walls);

    // Window rim (brushed metal) + dark gasket just inside it.
    const zr = Z_WIN + 0.05;
    const R = octAt(zr);
    for (let i = 0; i < n; i++) {
      if (i === FLOOR_EDGE) continue;
      const j = (i + 1) % n;
      const inward = edgeInward(i);
      const a = R[i].clone().addScaledVector(inward, 0.045);
      const b = R[j].clone().addScaledVector(inward, 0.045);
      const dir = new V3().subVectors(b, a).normalize();
      a.addScaledVector(dir, -0.05);
      b.addScaledVector(dir, 0.05);
      const { m, len } = beamMatrix(a, b, inward);
      metal.add(new THREE.BoxGeometry(0.12, 0.09, len), m);
      const ga = a.clone().addScaledVector(inward, 0.05).setZ(zr + 0.04);
      const gb = b.clone().addScaledVector(inward, 0.05).setZ(zr + 0.04);
      const gm = beamMatrix(ga, gb, inward);
      trim.add(new THREE.BoxGeometry(0.03, 0.02, gm.len), gm.m);
    }

    // Structural ribs with inset light strips.
    const ribs: { z: number; thick: number; across: number; skip: number[] }[] = [
      { z: -1.42, thick: 0.1, across: 0.15, skip: [FLOOR_EDGE] },
      { z: -1.0, thick: 0.08, across: 0.11, skip: [0, FLOOR_EDGE] },
    ];
    for (const rib of ribs) {
      const P = octAt(rib.z);
      for (let i = 0; i < n; i++) {
        if (rib.skip.includes(i)) continue;
        const j = (i + 1) % n;
        const inward = edgeInward(i);
        const a = P[i].clone().addScaledVector(inward, rib.thick / 2);
        const b = P[j].clone().addScaledVector(inward, rib.thick / 2);
        const dir = new V3().subVectors(b, a).normalize();
        a.addScaledVector(dir, -0.04);
        b.addScaledVector(dir, 0.04);
        const { m, len } = beamMatrix(a, b, inward);
        hull.add(new THREE.BoxGeometry(rib.across, rib.thick, len), m);
        // Rubber cap on the inner face.
        const ca = a.clone().addScaledVector(inward, rib.thick / 2 + 0.006);
        const cb = b.clone().addScaledVector(inward, rib.thick / 2 + 0.006);
        const cm = beamMatrix(ca, cb, inward);
        trim.add(new THREE.BoxGeometry(rib.across * 0.8, 0.012, cm.len - 0.02), cm.m);
        if (i !== 3 && i !== 5) {
          const la = a.clone().addScaledVector(inward, rib.thick / 2 + 0.013);
          const lb = b.clone().addScaledVector(inward, rib.thick / 2 + 0.013);
          const lm = beamMatrix(la, lb, inward);
          accent.add(new THREE.BoxGeometry(0.012, 0.004, lm.len - 0.12), lm.m);
        }
      }
    }

    // Angled pillars along the tunnel seams (A-pillars heaviest).
    const pillars: { i: number; across: number; thick: number }[] = [
      { i: 0, across: 0.1, thick: 0.07 },
      { i: 1, across: 0.1, thick: 0.07 },
      { i: 2, across: 0.2, thick: 0.13 },
      { i: 7, across: 0.2, thick: 0.13 },
      { i: 3, across: 0.14, thick: 0.09 },
      { i: 6, across: 0.14, thick: 0.09 },
    ];
    for (const p of pillars) {
      const inward = new V3(-WIN[p.i][0], -WIN[p.i][1], 0).normalize();
      const a = W[p.i].clone().addScaledVector(inward, p.thick / 2).setZ(Z_WIN + 0.05);
      const b = N[p.i].clone().addScaledVector(inward, p.thick / 2);
      const { m, len } = beamMatrix(a, b, inward);
      metal.add(new THREE.BoxGeometry(p.across, p.thick, len), m);
      // Rubber inner cover + cable conduit.
      const ta = a.clone().addScaledVector(inward, p.thick / 2 + 0.01);
      const tb = b.clone().addScaledVector(inward, p.thick / 2 + 0.01);
      const tm = beamMatrix(ta, tb, inward);
      trim.add(new THREE.BoxGeometry(p.across * 0.55, 0.02, tm.len - 0.1), tm.m);
      if (p.across > 0.15) {
        const side = new V3().crossVectors(inward, new V3().subVectors(b, a).normalize()).normalize();
        for (const off of [-1, 1]) {
          const ca = ta.clone().addScaledVector(side, off * p.across * 0.38).addScaledVector(inward, 0.005);
          const cb = tb.clone().addScaledVector(side, off * p.across * 0.38).addScaledVector(inward, 0.005);
          const cyl = new THREE.CylinderGeometry(0.012, 0.012, 1, 8, 1, true).rotateX(Math.PI / 2);
          const cm = beamMatrix(ca, cb, inward);
          cyl.scale(1, 1, cm.len - 0.12);
          trim.add(cyl, cm.m);
        }
        // Status lights along the pillar.
        for (let k = 0; k < 3; k++) {
          const f = 0.2 + k * 0.12;
          const lp = ta.clone().lerp(tb, f).addScaledVector(inward, 0.012);
          const lm = beamMatrix(lp.clone(), lp.clone().add(new V3().subVectors(tb, ta).normalize().multiplyScalar(0.01)), inward);
          this.led(mul(lm.m, new THREE.Matrix4().makeScale(0.018, 0.006, 0.012)), k === 0 ? WHITE : CYAN, k === 2 ? LedMode.Blink : LedMode.Pulse, 1.4 + k * 0.5);
        }
      }
    }

    // Annunciator housing hanging off the header.
    trim.box(0.7, 0.06, 0.05, trs(0, ANN_Y, ANN_Z - 0.03));
    metal.box(0.72, 0.01, 0.056, trs(0, ANN_Y - 0.034, ANN_Z - 0.03));
  }

  /** Main dashboard: sloped hood, glare-shield lip and a screen face angled at the pilot. */
  private buildDashboard(hull: Bin, trim: Bin, accent: Bin): void {
    const shape = new THREE.Shape();
    // (u, v) = (z, y)
    shape.moveTo(-1.85, -0.8);
    shape.lineTo(-1.15, -0.55);
    shape.lineTo(-1.135, -0.566);
    shape.lineTo(-1.175, -0.6);
    shape.lineTo(-0.95, -0.92);
    shape.lineTo(-0.8, -0.95);
    shape.lineTo(-0.8, -1.4);
    shape.lineTo(-1.85, -1.4);
    shape.closePath();
    const dash = new THREE.ExtrudeGeometry(shape, { depth: 3.0, bevelEnabled: false });
    const toWorld = new THREE.Matrix4().set(0, 0, 1, -1.5, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1);
    hull.add(dash, toWorld);

    // Glare-shield rubber lip.
    trim.box(3.0, 0.02, 0.05, trs(0, -0.556, -1.16, 0.3));

    // Face frame: x right, y up the face, z out of the face (towards the pilot).
    const top = new V3(0, -0.6, -1.175);
    const bot = new V3(0, -0.92, -0.95);
    const down = new V3().subVectors(bot, top).normalize();
    const fy = down.clone().negate();
    const fx = new V3(1, 0, 0);
    const fz = new V3().crossVectors(fx, fy);
    const face = new THREE.Matrix4().makeBasis(fx, fy, fz);
    face.setPosition(top.clone().addScaledVector(down, 0.115));
    const at = (x: number, y: number, z: number): THREE.Matrix4 => mul(face, trs(x, y, z));

    // Light strip under the glare shield, washing down over the screens.
    accent.box(1.34, 0.005, 0.006, at(0, 0.112, 0.03));

    // Screens: FLIGHT | RADAR | SYSTEMS.
    const H = 0.2;
    this.faceScreen(at(-0.43, 0.01, 0.016), 0.37, H, 512, 284, trim, (s, r, t) => this.drawFlight(s, r, t));
    this.faceScreen(at(0, 0.01, 0.016), 0.275, H, 512, 384, trim, (s, r, t) => this.drawRadar(s, r, t));
    this.faceScreen(at(0.43, 0.01, 0.016), 0.37, H, 512, 284, trim, (s, r, t) => this.drawSystems(s, r, t));

    // Button columns between the screens.
    for (const cx of [-0.19, 0.19]) {
      trim.box(0.07, 0.2, 0.012, at(cx, -0.004, 0.006));
      for (let k = 0; k < 4; k++) {
        const y = 0.07 - k * 0.048;
        trim.box(0.036, 0.026, 0.014, at(cx - 0.008, y, 0.016));
        const col = k === 0 ? GOLD : k === 3 ? '#5dffa8' : CYAN;
        this.led(mul(at(cx + 0.022, y, 0.014), new THREE.Matrix4().makeScale(0.008, 0.008, 0.004)), col, k === 1 ? LedMode.Blink : LedMode.Steady, 0.9 + k * 0.3);
      }
    }

    // A few details along the hood.
    for (const x of [-1.0, -0.75, 0.75, 1.0]) {
      hull.box(0.16, 0.012, 0.08, trs(x, -0.575, -1.3, -0.345));
    }
  }

  private faceScreen(
    m: THREE.Matrix4,
    w: number,
    h: number,
    cw: number,
    ch: number,
    trim: Bin,
    draw: Screen['draw'],
  ): void {
    // Bezel: back plate + raised lips.
    const pad = 0.014;
    trim.add(new THREE.BoxGeometry(w + pad * 2, h + pad * 2, 0.012), mul(m, trs(0, 0, -0.008)));
    trim.add(new THREE.BoxGeometry(w + pad * 2, pad, 0.012), mul(m, trs(0, h / 2 + pad / 2, 0.002)));
    trim.add(new THREE.BoxGeometry(w + pad * 2, pad, 0.012), mul(m, trs(0, -h / 2 - pad / 2, 0.002)));
    trim.add(new THREE.BoxGeometry(pad, h, 0.012), mul(m, trs(-w / 2 - pad / 2, 0, 0.002)));
    trim.add(new THREE.BoxGeometry(pad, h, 0.012), mul(m, trs(w / 2 + pad / 2, 0, 0.002)));

    const canvas = makeCanvas(cw, ch);
    const ctx = ctx2d(canvas);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false, color: 0xe6f2ff });
    this.opaque(mat);
    mat.side = THREE.FrontSide;
    const geo = new THREE.PlaneGeometry(w, h);
    const mesh = this.addMesh(geo, mat, RO_SCREEN);
    m.decompose(mesh.position, mesh.quaternion, mesh.scale);
    this.textures.push(tex);

    const screen: Screen = { canvas, ctx, tex, overlay: this.makeOverlay(cw, ch), w: cw, h: ch, draw };
    this.screens.push(screen);
  }

  private makeOverlay(w: number, h: number): HTMLCanvasElement {
    const c = makeCanvas(w, h);
    const g = ctx2d(c);
    g.fillStyle = 'rgba(0,0,0,0.2)';
    for (let y = 0; y < h; y += 4) g.fillRect(0, y, w, 1.5);
    const gr = g.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h) * 0.75);
    gr.addColorStop(0, 'rgba(0,0,0,0)');
    gr.addColorStop(1, 'rgba(0,0,0,0.45)');
    g.fillStyle = gr;
    g.fillRect(0, 0, w, h);
    // Glass sheen.
    const sh = g.createLinearGradient(0, 0, w, h);
    sh.addColorStop(0, 'rgba(255,255,255,0.05)');
    sh.addColorStop(0.35, 'rgba(255,255,255,0)');
    g.fillStyle = sh;
    g.fillRect(0, 0, w, h);
    return c;
  }

  /** Side consoles: NAV display on the left, throttle quadrant on the right. */
  private buildConsoles(hull: Bin, trim: Bin, metal: Bin, accent: Bin): void {
    const prof: [number, number][] = [
      [0.64, -0.6],
      [0.66, -0.53],
      [0.7, -0.51],
      [1.4, -0.4],
      [1.4, -1.4],
      [0.64, -1.4],
    ];
    const z0 = -1.35;
    const depth = 1.0;
    for (const side of [-1, 1]) {
      const shape = new THREE.Shape();
      prof.forEach(([x, y], i) => (i === 0 ? shape.moveTo(x * side, y) : shape.lineTo(x * side, y)));
      shape.closePath();
      const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
      g.translate(0, 0, z0);
      hull.add(g);
      // Rubber arm-rest edge + light strip along the inner lip.
      trim.box(0.05, 0.03, depth - 0.1, trs(0.68 * side, -0.515, z0 + depth / 2 + 0.05, 0, 0, side * 0.35));
      accent.box(0.004, 0.006, depth - 0.3, trs(0.643 * side, -0.57, z0 + depth / 2 + 0.1));
    }
    const slope = Math.atan2(0.11, 0.7);
    const topY = (x: number): number => -0.51 + ((Math.abs(x) - 0.7) / 0.7) * 0.11;

    // --- Right: throttle quadrant + button bank.
    const qx = 0.84;
    const qz = -0.74;
    const quad = trs(qx, topY(qx), qz, 0, 0, slope);
    trim.add(new THREE.BoxGeometry(0.11, 0.024, 0.34), mul(quad, trs(0, 0.012, 0)));
    metal.add(new THREE.BoxGeometry(0.02, 0.006, 0.3), mul(quad, trs(-0.035, 0.026, 0)));
    metal.add(new THREE.BoxGeometry(0.02, 0.006, 0.3), mul(quad, trs(0.035, 0.026, 0)));
    for (let k = 0; k < 10; k++) {
      const z = 0.1 - k * 0.022;
      this.led(mul(quad, trs(-0.048, 0.027, z)), k < 7 ? CYAN : k < 9 ? GOLD : WARN, LedMode.Throttle, 1, 0, k, [0.008, 0.004, 0.012]);
    }
    this.led(mul(quad, trs(-0.048, 0.027, 0.135)), WARN, LedMode.Reverse, 1, 0, 0, [0.008, 0.004, 0.012]);
    this.leverArm.position.set(0, 0.024, 0);
    const pivot = new THREE.Group();
    quad.decompose(pivot.position, pivot.quaternion, pivot.scale);
    pivot.add(this.leverArm);
    this.root.add(pivot);

    const bank = trs(1.04, topY(1.04), -0.9, 0, 0, slope);
    trim.add(new THREE.BoxGeometry(0.2, 0.012, 0.26), mul(bank, trs(0, 0.006, 0)));
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const x = -0.06 + c * 0.06;
        const z = -0.08 + r * 0.07;
        trim.add(new THREE.BoxGeometry(0.036, 0.014, 0.036), mul(bank, trs(x, 0.019, z)));
        const cols = [CYAN, '#5dffa8', GOLD];
        this.led(mul(bank, trs(x, 0.027, z - 0.01)), cols[(r + c) % 3], [LedMode.Steady, LedMode.Pulse, LedMode.Blink, LedMode.Flicker][(r * 3 + c) % 4], 0.8 + this.rand() * 1.6, this.rand(), 0, [0.02, 0.003, 0.006]);
      }
    }

    // --- Left: NAV display on a wedge mount facing the pilot.
    const holder = new THREE.Object3D();
    holder.position.set(-0.89, -0.378, -0.87);
    holder.lookAt(0, 0.08, 0);
    holder.updateMatrix();
    const nm = holder.matrix.clone();
    trim.add(new THREE.BoxGeometry(0.14, 0.11, 0.1), mul(nm, trs(0, -0.09, -0.06)));
    this.faceScreen(nm, 0.3, 0.19, 512, 324, trim, (s, r, t) => this.drawNav(s, r, t));
    for (let k = 0; k < 6; k++) {
      this.led(mul(nm, trs(-0.1 + k * 0.04, -0.118, 0.004)), k === 5 ? WARN : k === 0 ? WHITE : CYAN, k === 5 ? LedMode.Warning : k % 2 ? LedMode.Pulse : LedMode.Steady, 1.1 + k * 0.2, k * 0.17, 0, [0.022, 0.005, 0.004]);
    }
    const lbank = trs(-1.08, topY(1.08), -0.72, 0, 0, -slope);
    trim.add(new THREE.BoxGeometry(0.2, 0.014, 0.3), mul(lbank, trs(0, 0.007, 0)));
    for (let k = 0; k < 5; k++) {
      metal.add(new THREE.CylinderGeometry(0.014, 0.016, 0.02, 12), mul(lbank, trs(-0.07 + k * 0.035, 0.024, -0.06)));
      this.led(mul(lbank, trs(-0.07 + k * 0.035, 0.016, 0.0)), k === 2 ? GOLD : CYAN, LedMode.Flicker, 1, k * 0.31, 0, [0.01, 0.004, 0.01]);
    }
  }

  /** Overhead panel with toggle-switch banks, following the ceiling slope. */
  private buildOverhead(hull: Bin, trim: Bin, metal: Bin, accent: Bin): void {
    const zc = -0.97;
    const yTop = 0.8 * octScale(zc);
    const tilt = Math.atan((0.8 * (1 - NEAR_SCALE)) / (Z_NEAR - Z_WIN));
    const oh = trs(0, yTop - 0.045, zc, tilt);
    const at = (x: number, y: number, z: number, rx = 0): THREE.Matrix4 => mul(oh, trs(x, y, z, rx));

    hull.add(new THREE.BoxGeometry(1.06, 0.05, 0.62), at(0, 0, 0));
    trim.add(new THREE.BoxGeometry(0.98, 0.012, 0.54), at(0, -0.029, 0));
    // Edge light strips.
    for (const s of [-1, 1]) accent.add(new THREE.BoxGeometry(0.008, 0.006, 0.56), at(s * 0.535, -0.02, 0));
    // Grab bar along the front edge.
    metal.add(new THREE.CylinderGeometry(0.012, 0.012, 0.7, 10).rotateZ(Math.PI / 2), at(0, -0.075, 0.27));
    for (const s of [-1, 1]) metal.add(new THREE.BoxGeometry(0.02, 0.05, 0.02), at(s * 0.33, -0.05, 0.27));

    const cols = [CYAN, CYAN, '#5dffa8', GOLD, WHITE];
    const rows = [-0.19, -0.06, 0.07, 0.2];
    rows.forEach((z, r) => {
      // Row guard rail.
      metal.add(new THREE.BoxGeometry(0.86, 0.006, 0.006), at(0, -0.045, z - 0.03));
      for (let c = 0; c < 9; c++) {
        const x = -0.36 + c * 0.09;
        trim.add(new THREE.BoxGeometry(0.05, 0.012, 0.036), at(x, -0.041, z));
        const flip = this.rand() < 0.5 ? -1 : 1;
        metal.add(new THREE.BoxGeometry(0.008, 0.03, 0.008), at(x, -0.055, z + flip * 0.004, flip * 0.45));
        const roll = this.rand();
        const mode = roll < 0.55 ? LedMode.Steady : roll < 0.75 ? LedMode.Blink : roll < 0.9 ? LedMode.Pulse : LedMode.Flicker;
        this.led(at(x + 0.03, -0.037, z - 0.01), cols[Math.floor(this.rand() * cols.length)], mode, 0.6 + this.rand() * 2, this.rand(), r, [0.008, 0.003, 0.008]);
      }
    });
  }

  private buildAnnunciator(label: string, x: number, trim: Bin): Annunciator {
    const c = makeCanvas(256, 64);
    const g = ctx2d(c);
    const bg = g.createLinearGradient(0, 0, 0, 64);
    bg.addColorStop(0, '#6a6a6a');
    bg.addColorStop(1, '#3a3a3a');
    g.fillStyle = bg;
    g.fillRect(0, 0, 256, 64);
    g.strokeStyle = 'rgba(255,255,255,0.5)';
    g.lineWidth = 3;
    g.strokeRect(4, 4, 248, 56);
    g.fillStyle = '#ffffff';
    g.font = font(26);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(label, 128, 34);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.textures.push(tex);
    const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false, color: 0x202428 });
    this.opaque(mat);
    const geo = new THREE.PlaneGeometry(0.19, 0.0475);
    const mesh = this.addMesh(geo, mat, RO_DETAIL);
    mesh.position.set(x, ANN_Y, ANN_Z + 0.001);
    trim.box(0.2, 0.056, 0.006, trs(x, ANN_Y, ANN_Z - 0.006));
    return { mesh, mat };
  }

  private setAnn(a: Annunciator, color: string | null, level: number): void {
    if (!color) a.mat.color.setRGB(0.12, 0.13, 0.14);
    else a.mat.color.set(color).multiplyScalar(level);
  }

  private buildLever(): void {
    const armGeo = new THREE.BoxGeometry(0.02, 0.17, 0.022).translate(0, 0.085, 0);
    const arm = new THREE.Mesh(armGeo, this.metalMat);
    const knobGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.12, 20).rotateZ(Math.PI / 2).translate(-0.015, 0.182, 0);
    const knob = new THREE.Mesh(knobGeo, this.trimMat);
    const capGeo = new THREE.BoxGeometry(0.018, 0.006, 0.026).translate(0.035, 0.212, 0);
    const cap = new THREE.Mesh(capGeo, this.accentMat);
    const collarGeo = new THREE.CylinderGeometry(0.02, 0.026, 0.03, 12).translate(0, 0.012, 0);
    const collar = new THREE.Mesh(collarGeo, this.trimMat);
    for (const [mesh, g] of [
      [arm, armGeo],
      [knob, knobGeo],
      [cap, capGeo],
      [collar, collarGeo],
    ] as const) {
      mesh.renderOrder = RO_DETAIL;
      mesh.frustumCulled = false;
      this.geometries.push(g);
      this.leverArm.add(mesh);
    }
  }

  private buildGlass(): void {
    const shape = new THREE.Shape();
    WIN.forEach(([x, y], i) => (i === 0 ? shape.moveTo(x, y) : shape.lineTo(x, y)));
    shape.closePath();
    const geo = new THREE.ShapeGeometry(shape);
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      uv.setXY(i, (pos.getX(i) + 1.6) / 3.2, (pos.getY(i) + 0.95) / 1.75);
    }
    geo.translate(0, 0, Z_WIN - 0.005);
    const alpha = makeGlassAlpha(this.rand);
    this.textures.push(alpha);
    const mat = new THREE.MeshPhysicalMaterial({
      color: 0xa9d6ff,
      metalness: 0,
      roughness: 0.06,
      transparent: true,
      opacity: 0.24,
      alphaMap: alpha,
      depthWrite: false,
      envMapIntensity: 1.5,
      side: THREE.DoubleSide,
      forceSinglePass: true,
    });
    this.materials.push(mat);
    this.addMesh(geo, mat, RO_GLASS);
  }

  private led(
    m: THREE.Matrix4,
    color: string,
    mode: LedMode,
    period = 1,
    phase = 0,
    param = 0,
    size?: [number, number, number],
  ): void {
    const mm = size ? mul(m, new THREE.Matrix4().makeScale(size[0], size[1], size[2])) : m;
    this.leds.push({ color: new THREE.Color(color), mode, period, phase, param });
    this.ledMatrices.push(mm);
  }

  private readonly ledMatrices: THREE.Matrix4[] = [];

  private buildLedMesh(): void {
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshBasicMaterial({ toneMapped: false });
    this.opaque(mat);
    this.geometries.push(geo);
    this.ledMesh = new THREE.InstancedMesh(geo, mat, this.leds.length);
    this.ledMesh.renderOrder = RO_SCREEN;
    this.ledMesh.frustumCulled = false;
    this.ledMatrices.forEach((m, i) => {
      this.ledMesh.setMatrixAt(i, m);
      this.ledMesh.setColorAt(i, this.leds[i].color);
    });
    this.root.add(this.ledMesh);
  }

  private updateLeds(t: number, r: CockpitReadout): void {
    const c = this.tmpColor;
    for (let i = 0; i < this.leds.length; i++) {
      const L = this.leds[i];
      let b = 1;
      const cyc = t / L.period + L.phase;
      switch (L.mode) {
        case LedMode.Steady:
          b = 0.9;
          break;
        case LedMode.Blink:
          b = cyc % 1 < 0.5 ? 1 : 0.08;
          break;
        case LedMode.Pulse:
          b = 0.3 + 0.7 * (0.5 + 0.5 * Math.sin(cyc * Math.PI * 2));
          break;
        case LedMode.Flicker: {
          const k = Math.sin(Math.floor(t * 9 + L.phase * 50) * 12.9898) * 43758.5453;
          b = k - Math.floor(k) > 0.35 ? 1 : 0.15;
          break;
        }
        case LedMode.Throttle:
          b = r.throttle >= (L.param + 1) / 10 - 0.05 ? 1 : 0.06;
          break;
        case LedMode.Reverse:
          b = r.throttle < -0.01 ? 1 : 0.06;
          break;
        case LedMode.Warning:
          b = r.shield < 0.3 ? (t * 4) % 1 < 0.5 ? 1 : 0.1 : 0.06;
          break;
        case LedMode.Warp:
          b = r.warp === 'off' ? 0.06 : 1;
          break;
      }
      c.copy(L.color).multiplyScalar(b);
      this.ledMesh.setColorAt(i, c);
    }
    if (this.ledMesh.instanceColor) this.ledMesh.instanceColor.needsUpdate = true;
  }

  // -------------------------------------------------------------------------
  // Screen drawing
  // -------------------------------------------------------------------------

  private frame(s: Screen, title: string, right = '', rightColor = DIM): void {
    const { ctx: g, w, h } = s;
    const bg = g.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#07202c');
    bg.addColorStop(1, '#020a10');
    g.fillStyle = bg;
    g.fillRect(0, 0, w, h);
    g.strokeStyle = 'rgba(127,231,255,0.05)';
    g.lineWidth = 1;
    for (let x = 0; x < w; x += 32) {
      g.beginPath();
      g.moveTo(x + 0.5, 40);
      g.lineTo(x + 0.5, h);
      g.stroke();
    }
    for (let y = 40; y < h; y += 32) {
      g.beginPath();
      g.moveTo(0, y + 0.5);
      g.lineTo(w, y + 0.5);
      g.stroke();
    }
    g.fillStyle = 'rgba(127,231,255,0.12)';
    g.fillRect(0, 0, w, 38);
    g.fillStyle = CYAN;
    g.fillRect(0, 37, w, 2);
    g.font = font(24);
    g.textBaseline = 'middle';
    g.textAlign = 'left';
    g.fillStyle = CYAN;
    g.fillText(title, 14, 20);
    if (right) {
      g.textAlign = 'right';
      g.fillStyle = rightColor;
      g.fillText(right, w - 14, 20);
      g.textAlign = 'left';
    }
    // Corner brackets.
    g.strokeStyle = CYAN;
    g.lineWidth = 3;
    const k = 16;
    for (const [x, y, dx, dy] of [
      [3, 3, 1, 1],
      [w - 3, 3, -1, 1],
      [3, h - 3, 1, -1],
      [w - 3, h - 3, -1, -1],
    ]) {
      g.beginPath();
      g.moveTo(x, y + dy * k);
      g.lineTo(x, y);
      g.lineTo(x + dx * k, y);
      g.stroke();
    }
  }

  private label(g: CanvasRenderingContext2D, text: string, x: number, y: number, color = DIM, px = 18): void {
    g.font = font(px);
    g.fillStyle = color;
    g.textAlign = 'left';
    g.textBaseline = 'alphabetic';
    g.fillText(text, x, y);
  }

  private fit(g: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, px: number, color: string): void {
    let size = px;
    g.font = font(size);
    while (size > 14 && g.measureText(text).width > maxW) {
      size -= 2;
      g.font = font(size);
    }
    g.fillStyle = color;
    g.textAlign = 'left';
    g.textBaseline = 'alphabetic';
    g.fillText(text, x, y);
  }

  private drawFlight(s: Screen, r: CockpitReadout, t: number): void {
    const g = s.ctx;
    const boostOn = r.boosting && Math.floor(t * 4) % 2 === 0;
    this.frame(s, 'FLIGHT', r.boosting ? 'BOOST' : 'NOMINAL', r.boosting ? (boostOn ? WARN : GOLD) : DIM);
    this.label(g, 'VELOCITY', 18, 70);
    this.fit(g, r.speed, 16, 128, 380, 60, WHITE);
    g.fillStyle = FAINT;
    g.fillRect(16, 146, 380, 2);
    this.label(g, 'ALTITUDE', 18, 176);
    this.fit(g, r.altitude, 16, 214, 220, 34, CYAN);
    this.label(g, 'REF BODY', 246, 176);
    this.fit(g, r.nearest.toUpperCase(), 246, 214, 150, 30, GOLD);
    this.label(g, `THR ${Math.round(r.throttle * 100)}%`, 18, 258, r.throttle < 0 ? WARN : CYAN, 20);

    // Throttle tape.
    const x = 432;
    const y0 = 56;
    const y1 = s.h - 18;
    const bw = 44;
    const span = y1 - y0;
    const zeroY = y1 - (0.15 / 1.15) * span;
    g.strokeStyle = DIM;
    g.lineWidth = 2;
    g.strokeRect(x, y0, bw, span);
    const thr = THREE.MathUtils.clamp(r.throttle, -0.15, 1);
    const ty = zeroY - (thr / 1) * (zeroY - y0);
    if (thr >= 0) {
      const gr = g.createLinearGradient(0, zeroY, 0, y0);
      gr.addColorStop(0, 'rgba(127,231,255,0.35)');
      gr.addColorStop(1, r.boosting ? GOLD : CYAN);
      g.fillStyle = gr;
      g.fillRect(x + 4, ty, bw - 8, zeroY - ty);
    } else {
      g.fillStyle = WARN;
      g.fillRect(x + 4, zeroY, bw - 8, ty - zeroY);
    }
    g.fillStyle = WHITE;
    g.fillRect(x - 8, zeroY - 1, bw + 16, 2);
    for (let k = 1; k < 10; k++) {
      const yy = zeroY - (k / 10) * (zeroY - y0);
      g.fillStyle = DIM;
      g.fillRect(x + bw, yy, k % 5 === 0 ? 10 : 5, 1.5);
    }
    // Pointer.
    g.fillStyle = WHITE;
    g.beginPath();
    g.moveTo(x - 4, ty);
    g.lineTo(x - 16, ty - 8);
    g.lineTo(x - 16, ty + 8);
    g.closePath();
    g.fill();
  }

  private drawNav(s: Screen, r: CockpitReadout, t: number): void {
    const g = s.ctx;
    const secs = Math.max(0, Math.floor(r.timeSeconds));
    const hh = String(Math.floor(secs / 3600)).padStart(2, '0');
    const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    const ss = String(secs % 60).padStart(2, '0');
    this.frame(s, 'NAV / TARGET', `T+${hh}:${mm}:${ss}`);
    if (r.target) {
      this.label(g, 'TARGET LOCK', 18, 76, GOLD);
      this.fit(g, r.target.toUpperCase(), 16, 128, 330, 44, GOLD);
      g.fillStyle = FAINT;
      g.fillRect(16, 146, 330, 2);
      this.label(g, 'RANGE', 18, 180);
      this.fit(g, r.targetDistance, 16, 232, 330, 48, WHITE);
      // Reticle.
      const cx = 430;
      const cy = 170;
      const rot = t * 0.8;
      g.strokeStyle = GOLD;
      g.lineWidth = 3;
      for (let k = 0; k < 4; k++) {
        const a = rot + (k * Math.PI) / 2;
        g.beginPath();
        g.arc(cx, cy, 44, a, a + 0.9);
        g.stroke();
      }
      g.beginPath();
      g.moveTo(cx, cy - 12);
      g.lineTo(cx + 12, cy);
      g.lineTo(cx, cy + 12);
      g.lineTo(cx - 12, cy);
      g.closePath();
      g.stroke();
    } else {
      this.label(g, 'NO TARGET LOCK', 18, 80, DIM, 22);
      this.label(g, 'NEAREST BODY', 18, 130);
      this.fit(g, r.nearest.toUpperCase(), 16, 182, 470, 48, WHITE);
      this.label(g, 'ALT', 18, 228);
      this.fit(g, r.altitude, 70, 230, 300, 30, CYAN);
    }
    // Footer: contacts summary.
    const rocks = r.radar.filter((p) => p.kind === 'rock').length;
    const ships = r.radar.filter((p) => p.kind === 'player').length;
    g.fillStyle = 'rgba(127,231,255,0.1)';
    g.fillRect(0, s.h - 50, s.w, 50);
    this.label(g, `CONTACTS  ${ships} SHIP  ·  ${rocks} ROCK`, 18, s.h - 18, CYAN, 20);
  }

  private drawRadar(s: Screen, r: CockpitReadout, t: number): void {
    const g = s.ctx;
    let far = 0;
    for (const p of r.radar) far = Math.max(far, Math.hypot(p.x, p.z));
    const need = Math.max(2000, far * 1.1);
    const exp = Math.pow(10, Math.floor(Math.log10(need)));
    const range = [1, 2, 5, 10].map((k) => k * exp).find((v) => v >= need) ?? 10 * exp;
    const rangeTxt = range >= 1e6 ? `${(range / 1e6).toLocaleString('en-US')} Mm` : `${(range / 1000).toLocaleString('en-US')} km`;
    this.frame(s, 'SCAN', rangeTxt, CYAN);

    const cx = s.w / 2;
    const cy = 38 + (s.h - 38) / 2 + 4;
    const R = (s.h - 38) / 2 - 16;
    // Scope background.
    const bg = g.createRadialGradient(cx, cy, 0, cx, cy, R);
    bg.addColorStop(0, 'rgba(127,231,255,0.10)');
    bg.addColorStop(1, 'rgba(127,231,255,0.02)');
    g.fillStyle = bg;
    g.beginPath();
    g.arc(cx, cy, R, 0, Math.PI * 2);
    g.fill();
    // Forward view cone.
    g.fillStyle = 'rgba(127,231,255,0.07)';
    g.beginPath();
    g.moveTo(cx, cy);
    g.arc(cx, cy, R, -Math.PI / 2 - 0.65, -Math.PI / 2 + 0.65);
    g.closePath();
    g.fill();
    // Rings + cross.
    g.lineWidth = 1.5;
    for (let k = 1; k <= 3; k++) {
      g.strokeStyle = k === 3 ? DIM : FAINT;
      g.beginPath();
      g.arc(cx, cy, (R * k) / 3, 0, Math.PI * 2);
      g.stroke();
    }
    g.strokeStyle = FAINT;
    g.beginPath();
    g.moveTo(cx - R, cy);
    g.lineTo(cx + R, cy);
    g.moveTo(cx, cy - R);
    g.lineTo(cx, cy + R);
    g.stroke();
    // Sweep.
    const a = (t * 1.6) % (Math.PI * 2);
    const sweep = g.createConicGradient ? g.createConicGradient(a - 0.6, cx, cy) : null;
    if (sweep) {
      sweep.addColorStop(0, 'rgba(127,231,255,0)');
      sweep.addColorStop(0.095, 'rgba(127,231,255,0.28)');
      sweep.addColorStop(0.1, 'rgba(127,231,255,0)');
      g.fillStyle = sweep;
      g.beginPath();
      g.arc(cx, cy, R, 0, Math.PI * 2);
      g.fill();
    }
    // Contacts (target drawn last).
    const sorted = [...r.radar].sort((p, q) => (p.kind === 'target' ? 1 : 0) - (q.kind === 'target' ? 1 : 0));
    for (const p of sorted) {
      let px = p.x / range;
      let pz = p.z / range;
      const d = Math.hypot(px, pz);
      const clipped = d > 1;
      if (clipped) {
        px /= d;
        pz /= d;
      }
      const sx = cx + px * R;
      const sy = cy + pz * R;
      const ey = sy - THREE.MathUtils.clamp(p.y / range, -0.6, 0.6) * R * 0.6;
      const col = p.kind === 'target' ? GOLD : p.kind === 'player' ? WHITE : 'rgba(160,215,235,0.85)';
      g.strokeStyle = col;
      g.fillStyle = col;
      g.lineWidth = 1.5;
      if (!clipped && Math.abs(ey - sy) > 2) {
        g.beginPath();
        g.moveTo(sx, sy);
        g.lineTo(sx, ey);
        g.stroke();
        g.fillRect(sx - 3, sy - 0.75, 6, 1.5);
      }
      const y = clipped ? sy : ey;
      if (p.kind === 'rock') {
        g.beginPath();
        g.arc(sx, y, 3.5, 0, Math.PI * 2);
        g.fill();
      } else {
        const k = p.kind === 'target' ? 9 : 7;
        g.beginPath();
        g.moveTo(sx, y - k);
        g.lineTo(sx + k, y);
        g.lineTo(sx, y + k);
        g.lineTo(sx - k, y);
        g.closePath();
        if (p.kind === 'target') {
          g.lineWidth = 2.5;
          g.stroke();
          g.beginPath();
          g.arc(sx, y, 15 + 3 * Math.sin(t * 6), 0, Math.PI * 2);
          g.stroke();
        } else g.fill();
      }
    }
    // Own ship marker.
    g.fillStyle = CYAN;
    g.beginPath();
    g.moveTo(cx, cy - 9);
    g.lineTo(cx + 6, cy + 6);
    g.lineTo(cx, cy + 3);
    g.lineTo(cx - 6, cy + 6);
    g.closePath();
    g.fill();
    // Ring labels.
    this.label(g, rangeTxt, cx + R * 0.71 + 4, cy - R * 0.71 - 2, DIM, 16);
  }

  private drawSystems(s: Screen, r: CockpitReadout, t: number): void {
    const g = s.ctx;
    const low = r.shield < 0.3;
    const blink = Math.floor(t * 4) % 2 === 0;
    this.frame(s, 'SYSTEMS', low ? 'SHIELD LOW' : 'ALL NOMINAL', low ? (blink ? WARN : GOLD) : DIM);

    // Shield.
    this.label(g, 'SHIELD', 18, 72);
    const pct = Math.round(THREE.MathUtils.clamp(r.shield, 0, 1) * 100);
    g.font = font(34);
    g.textAlign = 'right';
    g.fillStyle = low ? WARN : WHITE;
    g.fillText(`${pct}%`, s.w - 18, 76);
    g.textAlign = 'left';
    const segs = 24;
    const x0 = 18;
    const bw = s.w - 36;
    const sw = bw / segs;
    for (let k = 0; k < segs; k++) {
      const on = (k + 0.5) / segs <= r.shield;
      g.fillStyle = on ? (low ? (blink ? WARN : '#b8502c') : CYAN) : 'rgba(127,231,255,0.1)';
      g.fillRect(x0 + k * sw + 1, 88, sw - 3, 26);
    }

    // Warp.
    g.fillStyle = FAINT;
    g.fillRect(18, 132, s.w - 36, 2);
    this.label(g, 'WARP DRIVE', 18, 166);
    let txt = 'OFF';
    let col = DIM;
    if (r.warp === 'spool') {
      txt = `CHARGING ${Math.round(r.warpCharge * 100)}%`;
      col = GOLD;
    } else if (r.warp === 'on') {
      txt = 'ACTIVE';
      col = CYAN;
    } else if (r.warp === 'locked') {
      txt = 'MASS-LOCKED';
      col = WARN;
    }
    this.fit(g, txt, 16, 212, s.w - 36, 40, col);
    const by = 232;
    g.strokeStyle = 'rgba(127,231,255,0.3)';
    g.lineWidth = 1.5;
    g.strokeRect(18, by, s.w - 36, 22);
    if (r.warp === 'spool') {
      g.fillStyle = GOLD;
      g.fillRect(21, by + 3, (s.w - 42) * THREE.MathUtils.clamp(r.warpCharge, 0, 1), 16);
    } else if (r.warp === 'on') {
      g.save();
      g.beginPath();
      g.rect(20, by + 2, s.w - 40, 18);
      g.clip();
      g.fillStyle = CYAN;
      const off = (t * 120) % 40;
      for (let x = -40; x < s.w; x += 40) {
        g.beginPath();
        g.moveTo(x + off, by + 2);
        g.lineTo(x + off + 16, by + 11);
        g.lineTo(x + off, by + 20);
        g.lineTo(x + off + 8, by + 20);
        g.lineTo(x + off + 24, by + 11);
        g.lineTo(x + off + 8, by + 2);
        g.closePath();
        g.fill();
      }
      g.restore();
    } else if (r.warp === 'locked') {
      g.save();
      g.beginPath();
      g.rect(20, by + 2, s.w - 40, 18);
      g.clip();
      g.strokeStyle = WARN;
      g.lineWidth = 5;
      for (let x = 0; x < s.w + 30; x += 16) {
        g.beginPath();
        g.moveTo(x, by + 22);
        g.lineTo(x + 20, by);
        g.stroke();
      }
      g.restore();
    }
  }
}
